import { createClient } from '@supabase/supabase-js';
import axios from 'axios';
import { Database } from '../types/database';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY || '';

// Security: Never log sensitive URLs or keys
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase configuration missing. Please set environment variables.');
}

// Create client for user authentication (uses anon key)
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Create client for data fetching (uses service role key to bypass RLS)
export const supabaseAdmin = supabaseServiceKey 
  ? createClient<Database>(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

// Attach the real Supabase session token to every axios request to our own API.
// Several components call the default axios instance directly (no shared client),
// so this is registered once, here, rather than repeated at each call site.
// Same-origin only — never attach the user's session token to a third-party request.
axios.interceptors.request.use(async (config) => {
  const resolvedUrl = new URL(config.url || '', window.location.origin);
  if (resolvedUrl.origin !== window.location.origin) {
    return config;
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// Helper function to get the appropriate client
export const getSupabaseClient = (requireAdmin = false) => {
  if (requireAdmin && !supabaseAdmin) {
    console.warn('Admin client requested but service key not available, falling back to regular client');
    return supabase;
  }
  return requireAdmin ? supabaseAdmin! : supabase;
};